import { ref, onUnmounted } from 'vue'

export interface TableStatusPollingConfig<T = any> {
  /** 获取表格数据列表 */
  getTableData: () => T[]
  /** 获取行的唯一标识（用于匹配和更新） */
  getRowKey: (row: T) => string | number | undefined
  /** 判断是否需要轮询（接收行数据，可基于多个字段判断） */
  shouldPoll: (row: T) => boolean
  /** 轮询 API 调用函数，接收 rowKeys 数组，返回包含 list 的响应对象 */
  pollApi: (rowKeys: (string | number)[]) => Promise<{ list: T[] }>
  /** 更新单行数据（按 rowKey 匹配后更新对应行，可保留选中状态等特殊属性） */
  updateRow?: (oldRow: T, newRow: T) => T
  /** 需要同步的目标列表（单个或多个），composable 会自动按 rowKey 匹配更新 */
  syncTargetList?: (() => T[]) | (() => T[])[]
  /** 自定义同步状态的函数 */
  syncState?: () => void
  /** 轮询间隔（毫秒），默认 3000 */
  interval?: number
  /** 是否启用防抖（防止并发请求），默认 true */
  enableThrottle?: boolean
}

/**
 * 表格状态轮询 Composable
 *
 * @example
 * // TableMemoryPagination 场景（单个数据源）
 * const { startPolling } = useTableStatusPolling({
 *   getTableData: () => tableData.list,
 *   getRowKey: (row) => row.id,
 *   shouldPoll: (row) => DYNAMIC_STATUS.includes(row.status),
 *   pollApi: (rowKeys) => service.getListByServer({ ids: rowKeys, page: 1, limit: 9999 }),
 *   updateRow: (oldRow, newRow) => ({ ...newRow, $selected: oldRow.$selected }),
 *   syncTargetList: () => tableData.state.data.list
 * })
 *
 * @example
 * // 多个数据源同步
 * const { startPolling } = useTableStatusPolling({
 *   getTableData: () => tableData.list,
 *   getRowKey: (row) => row.id,
 *   shouldPoll: (row) => DYNAMIC_STATUS.includes(row.status),
 *   pollApi: (rowKeys) => service.getListByServer({ ids: rowKeys, page: 1, limit: 9999 }),
 *   updateRow: (oldRow, newRow) => ({ ...newRow, $selected: oldRow.$selected }),
 *   syncTargetList: [
 *     () => tableData.state.data.list,
 *     () => anotherDataList.value,
 *     () => cacheData.list
 *   ]
 * })
 *
 * @example
 * // CmTable 场景
 * const { startPolling } = useTableStatusPolling({
 *   getTableData: () => tableRef.value?.rowData?.list || [],
 *   getRowKey: (row) => row.device_id,
 *   shouldPoll: (row) => row.physical_exam_status === EXAMINATION_LOADING,
 *   pollApi: (rowKeys) => examinationService.getExaminationResult({ device_ids: rowKeys }),
 *   updateRow: (oldRow, newRow) => {
 *     Object.assign(oldRow, newRow)
 *     return oldRow
 *   }
 * })
 *
 * @example
 * // div 卡片列表（非表格）
 * const cardList = reactive({ items: [] })
 * const { startPolling } = useTableStatusPolling({
 *   getTableData: () => cardList.items,
 *   getRowKey: (row) => row.id,
 *   shouldPoll: (row) => [1, 2].includes(row.status),
 *   pollApi: (rowKeys) => api.getCardStatus({ ids: rowKeys }),
 *   updateRow: (oldRow, newRow) => Object.assign(oldRow, newRow) && oldRow
 * })
 */
export function useTableStatusPolling<T = any>(config: TableStatusPollingConfig<T>) {
  const {
    getTableData,
    getRowKey,
    shouldPoll,
    pollApi,
    updateRow = (oldRow, newRow) => ({ ...oldRow, ...newRow }),
    syncTargetList,
    syncState,
    interval = 3000,
    enableThrottle = true
  } = config

  let pollingInterval: ReturnType<typeof setInterval> | null = null
  const isPolling = ref(false)
  const stopLoop = ref(false)

  const executePoll = (): Promise<void> => {
    if (enableThrottle && stopLoop.value) {
      return Promise.resolve()
    }

    const tableData = getTableData()
    const pollingRows = tableData.filter(row => {
      const rowKey = getRowKey(row)
      if (rowKey === undefined) return false
      return shouldPoll(row)
    })

    if (pollingRows.length === 0) {
      stopPolling()
      return Promise.resolve()
    }

    if (enableThrottle) {
      stopLoop.value = true
    }

    const pollingKeys = pollingRows.map(row => getRowKey(row)!).filter(key => key !== undefined)
    if (pollingKeys.length === 0) {
      if (enableThrottle) {
        stopLoop.value = false
      }
      return Promise.resolve()
    }

    return pollApi(pollingKeys).then(response => {
      if (!response?.list?.length) return

      const currentTableData = getTableData()

      response.list.forEach((newRow: T) => {
        const rowKey = getRowKey(newRow)
        if (rowKey === undefined) return

        const index = currentTableData.findIndex(row => getRowKey(row) === rowKey)
        if (index === -1) return

        currentTableData[index] = updateRow(currentTableData[index], newRow)
      })

      if (syncTargetList) {
        const targetLists = Array.isArray(syncTargetList) ? syncTargetList.map(fn => fn()) : [syncTargetList()]

        targetLists.forEach(targetList => {
          response.list.forEach((newRow: T) => {
            const rowKey = getRowKey(newRow)
            if (rowKey === undefined) return

            const targetIndex = targetList.findIndex(row => getRowKey(row) === rowKey)
            if (targetIndex === -1) return

            const updatedRow = currentTableData.find(row => getRowKey(row) === rowKey)
            if (updatedRow) {
              targetList[targetIndex] = updatedRow
            }
          })
        })
      }

      syncState?.()
    }).catch(error => {
      console.error('轮询状态失败:', error)
    }).finally(() => {
      if (enableThrottle) {
        stopLoop.value = false
      }
    })
  }

  const startPolling = (): Promise<void> => {
    stopPolling()

    const tableData = getTableData()
    const pollingRows = tableData.filter(row => {
      const rowKey = getRowKey(row)
      if (rowKey === undefined) return false
      return shouldPoll(row)
    })

    if (pollingRows.length === 0) {
      return Promise.resolve()
    }

    isPolling.value = true
    pollingInterval = setInterval(() => {
      executePoll()
    }, interval)

    return executePoll()
  }

  const stopPolling = () => {
    if (!pollingInterval) return

    clearInterval(pollingInterval)
    pollingInterval = null
    isPolling.value = false
    stopLoop.value = false
  }

  const restartPolling = () => {
    stopPolling()
    startPolling()
  }

  onUnmounted(() => {
    stopPolling()
  })

  return {
    isPolling,
    startPolling,
    stopPolling,
    restartPolling,
    executePoll
  }
}

